import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Menu, MenuItem, ListItemIcon, Chip, Badge, useMediaQuery, useTheme, Tooltip, Button } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StreamIcon from '@mui/icons-material/Stream';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FilterListIcon from '@mui/icons-material/FilterList';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import PersonIcon from '@mui/icons-material/Person';
import { signOut } from '../../features/auth/authSlice';
import { StyledHeader } from '../../styles/styledComponents';
import DatePickerPopup from '../calendar/DatePickerPopup';

const Header = ({ 
  currentDate, 
  onMonthChange, 
  onAuthClick, 
  onRequireAuth,
  monthNames,
  today 
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.auth);
  const filters = useSelector((state) => state.filters);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const open = Boolean(anchorEl);
  const settingsOpen = Boolean(settingsAnchorEl);

  // 月の制限をチェック
  const { canGoPrev, canGoNext } = useMemo(() => {
    const oneYearAgo = new Date(today);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const threeMonthsAhead = new Date(today);
    threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);
    
    const prevMonth = new Date(currentDate);
    prevMonth.setMonth(prevMonth.getMonth() - 1);
    
    const nextMonth = new Date(currentDate);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    
    return {
      canGoPrev: prevMonth >= oneYearAgo,
      canGoNext: nextMonth <= threeMonthsAhead
    };
  }, [currentDate, today]);

  const handleAuthClick = useCallback((event) => {
    if (user) {
      setAnchorEl(event.currentTarget);
    } else {
      onAuthClick();
    }
  }, [user, onAuthClick]);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogout = useCallback(() => {
    handleClose();
    dispatch(signOut());
  }, [handleClose, dispatch]);

  const handleSettingsClick = useCallback((event) => {
    if (!user) {
      onRequireAuth();
      return;
    }
    setSettingsAnchorEl(event.currentTarget);
  }, [user, onRequireAuth]);

  const handleSettingsClose = useCallback(() => {
    setSettingsAnchorEl(null);
  }, []);

  const handleUserSettings = useCallback(() => {
    if (!user) {
      onRequireAuth();
      return;
    }
    handleSettingsClose();
    navigate('/settings', { replace: true });
  }, [user, navigate, onRequireAuth, handleSettingsClose]);

  const handleGoogleCalendarSettings = useCallback(() => {
    handleSettingsClose();
    // TODO: Googleカレンダー設定の実装
  }, [handleSettingsClose]);

  const handleFilterSettings = useCallback(() => {
    handleSettingsClose();
    navigate('/filters');
  }, [handleSettingsClose, navigate]);

  const totalFilterCount = filters.tournamentNames.length + filters.teamNames.length + filters.gameTitles.length;

  const handleDatePickerOpen = useCallback(() => {
    setDatePickerOpen(true);
  }, []);

  const handleDatePickerClose = useCallback(() => {
    setDatePickerOpen(false);
  }, []);

  const handleDateSelect = useCallback((date) => {
    onMonthChange(date);
  }, [onMonthChange]);

  return (
    <StyledHeader>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        maxWidth: 'xl',
        mx: 'auto',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(90deg, transparent, rgba(0, 255, 255, 0.1), transparent)',
          animation: 'scan 3s linear infinite',
          pointerEvents: 'none',
        },
        '@keyframes scan': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      }}>
        <Typography 
          variant={isMobile ? "h6" : "h4"}
          component="h1"
          fontWeight="900"
          sx={{
            background: 'linear-gradient(90deg, #00ffff 0%, #ff00ff 25%, #00ff00 50%, #ff00ff 75%, #00ffff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            backgroundSize: '300% 100%',
            animation: 'subtleGradient 8s ease-in-out infinite',
            letterSpacing: '0.1em',
            fontSize: isMobile ? '1.2rem' : '1.8rem',
            userSelect: 'none',
            textTransform: 'uppercase',
            position: 'relative',
            filter: 'drop-shadow(0 0 15px rgba(0, 255, 255, 0.3))',
            transition: 'all 0.4s ease',
            '@keyframes subtleGradient': {
              '0%, 100%': { 
                backgroundPosition: '0% 50%',
                filter: 'drop-shadow(0 0 15px rgba(0, 255, 255, 0.3))',
              },
              '50%': { 
                backgroundPosition: '100% 50%',
                filter: 'drop-shadow(0 0 20px rgba(255, 0, 255, 0.4))',
              },
            },
            '&:hover': {
              letterSpacing: '0.15em',
              filter: 'drop-shadow(0 0 25px rgba(0, 255, 255, 0.6))',
              transform: 'translateY(-1px)',
            },
          }}
        >
          CLASHSYNC
        </Typography>
        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, alignItems: 'center' }}>
          {filters.isActive && !isMobile && (
            <Chip
              icon={<FilterListIcon />}
              label={`フィルター: ${totalFilterCount}件`}
              size="small"
              onClick={() => navigate('/filters')}
              sx={{
                cursor: 'pointer',
                backgroundColor: 'rgba(255, 0, 255, 0.1)',
                borderColor: theme.palette.neon.magenta,
                color: theme.palette.neon.magenta,
                fontWeight: 700,
                letterSpacing: '0.05em',
                '&:hover': {
                  backgroundColor: 'rgba(255, 0, 255, 0.2)',
                  boxShadow: `0 0 20px ${theme.palette.neon.magenta}`,
                },
              }}
            />
          )}
          <Tooltip title="設定" placement="bottom" arrow>
            <IconButton
              sx={{
                bgcolor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(16px)',
                border: 2,
                borderColor: 'rgba(0, 255, 255, 0.3)',
                color: theme.palette.neon.cyan,
                '&:hover': { 
                  bgcolor: 'rgba(0, 255, 255, 0.1)',
                  borderColor: theme.palette.neon.cyan,
                  transform: 'scale(1.1) rotate(180deg)',
                  boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
                },
                transition: 'all 0.3s ease',
                width: isMobile ? 36 : 40,
                height: isMobile ? 36 : 40,
              }}
              onClick={handleSettingsClick}
              aria-controls={settingsOpen ? 'settings-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={settingsOpen ? 'true' : undefined}
            >
              <Badge badgeContent={filters.isActive ? totalFilterCount : 0} 
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: theme.palette.neon.magenta,
                    color: '#000',
                    fontWeight: 700,
                    boxShadow: `0 0 10px ${theme.palette.neon.magenta}`,
                  }
                }}
              >
                <SettingsIcon fontSize={isMobile ? "small" : "medium"} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title={user ? "アカウント" : "ログイン"} placement="bottom" arrow>
            <IconButton
              sx={{
                bgcolor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(16px)',
                border: 2,
                borderColor: user ? theme.palette.neon.lime : 'rgba(255, 255, 255, 0.3)',
                color: user ? theme.palette.neon.lime : '#fff',
                '&:hover': { 
                  bgcolor: user ? 'rgba(0, 255, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                  borderColor: user ? theme.palette.neon.lime : theme.palette.neon.cyan,
                  transform: 'scale(1.1)',
                  boxShadow: user ? `0 0 20px ${theme.palette.neon.lime}` : `0 0 20px ${theme.palette.neon.cyan}`,
                },
                transition: 'all 0.3s ease',
                width: isMobile ? 36 : 40,
                height: isMobile ? 36 : 40,
              }}
              onClick={handleAuthClick}
              aria-controls={open ? 'account-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
            >
              <AccountCircleIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
          
          {/* 設定メニュー */}
          <Menu
            id="settings-menu"
            anchorEl={settingsAnchorEl}
            open={settingsOpen}
            onClose={handleSettingsClose}
            onClick={handleSettingsClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                backgroundColor: 'rgba(10, 10, 10, 0.95)',
                border: '2px solid rgba(0, 255, 255, 0.3)',
                boxShadow: '0 0 30px rgba(0, 255, 255, 0.5)',
                minWidth: 250,
                '& .MuiMenuItem-root': {
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 255, 0.1)',
                    color: theme.palette.neon.cyan,
                    paddingLeft: 3,
                  },
                },
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleGoogleCalendarSettings} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <CalendarMonthIcon fontSize="small" sx={{ color: theme.palette.neon.cyan }} />
              </ListItemIcon>
              <Typography variant="body1">Googleカレンダー連携</Typography>
            </MenuItem>
            <MenuItem onClick={handleFilterSettings} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <Badge badgeContent={filters.isActive ? totalFilterCount : 0} 
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: theme.palette.neon.magenta,
                      color: '#000',
                      fontWeight: 700,
                    }
                  }}
                >
                  <StreamIcon fontSize="small" sx={{ color: theme.palette.neon.magenta }} />
                </Badge>
              </ListItemIcon>
              <Typography variant="body1">
                フィルター設定
                {filters.isActive && (
                  <Typography component="span" variant="caption" sx={{ ml: 1, color: theme.palette.neon.magenta }}>
                    ({totalFilterCount}件)
                  </Typography>
                )}
              </Typography>
            </MenuItem>
          </Menu>

          {/* ユーザーメニュー */}
          <Menu
            id="account-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                backgroundColor: 'rgba(10, 10, 10, 0.95)',
                border: '2px solid rgba(0, 255, 0, 0.3)',
                boxShadow: '0 0 30px rgba(0, 255, 0, 0.5)',
                minWidth: 250,
                '& .MuiMenuItem-root': {
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 0, 0.1)',
                    color: theme.palette.neon.lime,
                    paddingLeft: 3,
                  },
                },
              }
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {user?.email === 'reiji.01101114@gmail.com' && (
              <MenuItem component={Link} to="/admin/tournaments" sx={{ py: 1.5 }}>
                <ListItemIcon>
                  <AdminPanelSettingsIcon fontSize="small" sx={{ color: theme.palette.neon.orange }} />
                </ListItemIcon>
                <Typography variant="body1">管理者パネル</Typography>
              </MenuItem>
            )}
            <MenuItem onClick={handleUserSettings} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <PersonIcon fontSize="small" sx={{ color: theme.palette.neon.lime }} />
              </ListItemIcon>
              <Typography variant="body1">ユーザー設定</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
              <ListItemIcon>
                <PowerSettingsNewIcon fontSize="small" sx={{ color: theme.palette.neon.pink }} />
              </ListItemIcon>
              <Typography variant="body1">ログアウト</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        gap: isMobile ? 1 : 2,
        mt: 2,
        position: 'relative',
      }}>
        <Tooltip 
          title={!canGoPrev ? "制限: 1年前まで" : ""} 
          placement="top"
          disableHoverListener={canGoPrev}
        >
          <span>
            <IconButton 
              onClick={() => onMonthChange('prev')}
              disabled={!canGoPrev}
              sx={{ 
                bgcolor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(12px)',
                border: 2,
                borderColor: !canGoPrev ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 255, 255, 0.3)',
                color: !canGoPrev ? 'rgba(255, 255, 255, 0.3)' : theme.palette.neon.cyan,
                '&:hover': { 
                  bgcolor: !canGoPrev ? 'rgba(20, 20, 20, 0.8)' : 'rgba(0, 255, 255, 0.1)',
                  borderColor: !canGoPrev ? 'rgba(255, 255, 255, 0.1)' : theme.palette.neon.cyan,
                  transform: !canGoPrev ? 'none' : 'translateX(-5px)',
                  boxShadow: !canGoPrev ? 'none' : `0 0 20px ${theme.palette.neon.cyan}`,
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(20, 20, 20, 0.5)',
                  color: 'rgba(255, 255, 255, 0.2)',
                },
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          onClick={handleDatePickerOpen}
          sx={{
            color: 'text.primary',
            letterSpacing: '0.1em',
            fontWeight: 800,
            minWidth: isMobile ? '180px' : '250px',
            fontSize: isMobile ? '1.1rem' : '1.5rem',
            textTransform: 'uppercase',
            position: 'relative',
            p: 1,
            borderRadius: 2,
            '&:hover': {
              bgcolor: 'rgba(0, 255, 255, 0.05)',
              boxShadow: `0 0 20px ${theme.palette.neon.cyan}`,
              '& .year-text': {
                textShadow: `0 0 20px ${theme.palette.neon.cyan}`,
              },
              '& .month-text': {
                textShadow: `0 0 20px ${theme.palette.neon.magenta}`,
              },
            },
            transition: 'all 0.3s ease',
          }}
        >
          <span className="year-text neon-glow-cyan">
            {currentDate.getFullYear()}
          </span>
          {' '}
          <span className="month-text" style={{ color: theme.palette.neon.magenta }}>
            {monthNames[currentDate.getMonth()]}
          </span>
        </Button>
        <Tooltip 
          title={!canGoNext ? "制限: 3ヶ月先まで" : ""} 
          placement="top"
          disableHoverListener={canGoNext}
        >
          <span>
            <IconButton 
              onClick={() => onMonthChange('next')}
              disabled={!canGoNext}
              sx={{ 
                bgcolor: 'rgba(20, 20, 20, 0.8)',
                backdropFilter: 'blur(12px)',
                border: 2,
                borderColor: !canGoNext ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 255, 255, 0.3)',
                color: !canGoNext ? 'rgba(255, 255, 255, 0.3)' : theme.palette.neon.cyan,
                '&:hover': { 
                  bgcolor: !canGoNext ? 'rgba(20, 20, 20, 0.8)' : 'rgba(0, 255, 255, 0.1)',
                  borderColor: !canGoNext ? 'rgba(255, 255, 255, 0.1)' : theme.palette.neon.cyan,
                  transform: !canGoNext ? 'none' : 'translateX(5px)',
                  boxShadow: !canGoNext ? 'none' : `0 0 20px ${theme.palette.neon.cyan}`,
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(20, 20, 20, 0.5)',
                  color: 'rgba(255, 255, 255, 0.2)',
                },
                width: isMobile ? '36px' : '44px',
                height: isMobile ? '36px' : '44px',
                transition: 'all 0.3s ease'
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      
      {/* モバイル用フィルター表示 */}
      {isMobile && filters.isActive && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center',
          mt: 1,
          px: 1
        }}>
          <Chip
            icon={<FilterListIcon />}
            label={`フィルター: ${totalFilterCount}件 適用中`}
            size="small"
            onClick={() => navigate('/filters')}
            sx={{
              cursor: 'pointer',
              backgroundColor: 'rgba(255, 0, 255, 0.1)',
              borderColor: theme.palette.neon.magenta,
              color: theme.palette.neon.magenta,
              fontSize: '0.7rem',
              height: 24,
              fontWeight: 700,
              letterSpacing: '0.05em',
              '&:hover': {
                backgroundColor: 'rgba(255, 0, 255, 0.2)',
                boxShadow: `0 0 15px ${theme.palette.neon.magenta}`,
              },
            }}
          />
        </Box>
      )}
      
      {/* 日付選択ポップアップ */}
      <DatePickerPopup
        open={datePickerOpen}
        onClose={handleDatePickerClose}
        onDateSelect={handleDateSelect}
        currentDate={currentDate}
        today={today}
        monthNames={monthNames}
      />
    </StyledHeader>
  );
};

export default Header;
